import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const SalaryTab = ({ employeeId }) => {
  const [loading, setLoading] = useState(false);
  const [salaryStructure, setSalaryStructure] = useState(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [basic, setBasic] = useState('');
  const [hra, setHra] = useState('');
  
  // Default to 1st of current month so it applies to current payroll run
  const defaultDate = new Date();
  defaultDate.setDate(1);
  const [effectiveDate, setEffectiveDate] = useState(defaultDate.toISOString().split('T')[0]);

  const fetchSalary = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/salary/${employeeId}`);
      setSalaryStructure(res.data);
      setBasic(res.data.basic / 100);
      setHra(res.data.hra / 100);
      if (res.data.effectiveFrom) {
        setEffectiveDate(new Date(res.data.effectiveFrom).toISOString().split('T')[0]);
      }
    } catch (err) {
      // If not found, that's fine
      setSalaryStructure(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSalary();
  }, [employeeId]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/salary/${employeeId}`, {
        basic: basic * 100,
        hra: hra * 100,
        allowances: [],
        effectiveFrom: effectiveDate,
        changeReason: 'Manual Update via UI'
      });
      alert('Salary updated successfully');
      setIsEditing(false);
      fetchSalary();
    } catch (err) {
      console.error(err);
      alert('Failed to update salary');
    }
    setLoading(false);
  };

  if (loading && !salaryStructure && !isEditing) {
    return <div className="p-6 text-center text-gray-500">Loading salary data...</div>;
  }

  return (
    <div className="p-6 bg-white border border-gray-200 rounded-xl mt-6 mb-8 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 w-full">Salary Structure</h3>
      </div>
      
      {!isEditing ? (
        <div>
          {salaryStructure ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Basic Pay (Monthly)</p>
                <p className="text-xl font-bold text-gray-900">₹{(salaryStructure.basic / 100).toFixed(2)}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">HRA (Monthly)</p>
                <p className="text-xl font-bold text-gray-900">₹{(salaryStructure.hra / 100).toFixed(2)}</p>
              </div>
              <div className="col-span-2 text-sm text-gray-500 mt-2">
                Effective From: {new Date(salaryStructure.effectiveFrom).toLocaleDateString()}
              </div>
              <div className="col-span-2 mt-2 text-right">
                <button onClick={() => {
                  setIsEditing(true);
                  const d = new Date();
                  d.setDate(1);
                  setEffectiveDate(d.toISOString().split('T')[0]);
                }} className="px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium rounded-lg transition-colors border border-indigo-200">
                  Update Salary
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500 mb-4">No salary structure defined for this employee.</p>
              <button onClick={() => setIsEditing(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-sm">
                Set Salary Now
              </button>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Basic Pay (₹)</label>
              <input 
                type="number" 
                min="0"
                required
                value={basic === 0 && !isEditing ? '' : basic} 
                onChange={e => setBasic(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">HRA (₹)</label>
              <input 
                type="number" 
                min="0"
                required
                value={hra === 0 && !isEditing ? '' : hra} 
                onChange={e => setHra(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective From Date</label>
              <input 
                type="date" 
                required
                value={effectiveDate} 
                onChange={e => setEffectiveDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">If this is after the 1st of the month, the payroll engine won't apply it to this month's payroll!</p>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button 
              type="button" 
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-sm disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Structure'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SalaryTab;
